import React from "react";
import { Brand, Button, ConfirmDialog, Panel, Pill, formatRelative, When } from "@ozpanel/ui";
import { api, type Json, TokenSignIn, useRecoveryCredential } from "./recoveryCredential";

// RecoveryScreen — the console for a node whose panel has no platform database.
//
// Signing in needs the database, so this screen does not use a session at all: it authenticates every
// call with the node's RECOVERY TOKEN (root-only, /etc/ozpanel/secrets/recovery_token, minted with
// `ozpanel admin recovery-token`), kept in this tab's sessionStorage only. The server accepts that token
// on the /recovery/* routes and nowhere else, and writes every action taken with it to the node's own
// recovery log — the audit log is in the database that is down.
//
// What it shows is measured on the node without the database (modules/recovery/state.go). Where a fact
// is the node's local COPY of cluster state, the screen says so and how old the copy is.

/** GET /recovery/state (modules/recovery/state.go). */
type RecoveryState = {
  node: { name: string; fqdn: string; uuid: string };
  measured_at: string;
  panel: { database: Json; started_without_database: boolean };
  declaration: Json;
  galera: Json;
  storage: Json;
  services: Json[];
  peers: Json;
  credential?: { kind: string; allows: string; who: string; grant?: string };
};

export function RecoveryScreen() {
  const { token, signIn, signOut } = useRecoveryCredential();
  const [state, setState] = React.useState<RecoveryState | null>(null);
  const [stateErr, setStateErr] = React.useState<string | null>(null);
  const [run, setRun] = React.useState<Json | null>(null);
  const [actions, setActions] = React.useState<Json[]>([]);
  const [confirmApply, setConfirmApply] = React.useState(false);
  const [force, setForce] = React.useState(false);
  const [busy, setBusy] = React.useState(false);

  const load = React.useCallback(async () => {
    if (!token) return;
    try {
      const [st, rn, ac] = await Promise.all([
        api<RecoveryState>("/recovery/state"),
        api<{ run: Json | null }>("/recovery/run"),
        api<{ actions: Json[] }>("/recovery/actions"),
      ]);
      setState(st);
      setRun(rn.run);
      setActions(ac.actions ?? []);
      setStateErr(null);
    } catch (e) {
      const err = e as Error & { status?: number };
      if (err.status === 401) {
        signOut();
        setStateErr(err.message);
        return;
      }
      setStateErr(err.message);
    }
  }, [token]);

  React.useEffect(() => {
    void load();
    if (!token) return;
    // Faster while a run is in progress, so its account reads as it is written.
    const t = window.setInterval(() => void load(), run?.running ? 2000 : 10000);
    return () => window.clearInterval(t);
  }, [load, token, run?.running]);

  async function start(apply: boolean) {
    setBusy(true);
    try {
      await api("/recovery/run", { method: "POST", body: JSON.stringify({ apply, force: apply && force }) });
      await load();
    } catch (e) {
      setStateErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  if (!token) {
    return (
      <TokenSignIn
        title="Recovery mode"
        subtitle="For a node whose platform database is down."
        error={stateErr}
        onSignIn={(t) => {
          setStateErr(null);
          signIn(t);
        }}
      />
    );
  }

  const node: Partial<RecoveryState["node"]> = state?.node ?? {};
  const db = state?.panel?.database ?? {};
  const decl = state?.declaration ?? {};
  const gal = state?.galera ?? {};
  const storage = state?.storage ?? {};
  const peers = state?.peers ?? {};
  const services: Json[] = state?.services ?? [];
  const cred: Partial<NonNullable<RecoveryState["credential"]>> = state?.credential ?? {};
  const canRecover = cred.allows === "recover" || cred.allows === "force";
  const canForce = cred.allows === "force";
  const down = services.filter((s) => !s.active && !s.optional);

  return (
    <main className="mx-auto max-w-[1100px] p-4 sm:p-6">
      <header className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Brand />
          <div>
            <h1 className="text-[18px] font-[680]">Recovery mode · {node.name ?? "…"}</h1>
            <p className="text-[12.5px] text-muted">
              {node.fqdn} · measured {state?.measured_at ? formatRelative(state.measured_at) : "…"}
            </p>
            {cred.kind && (
              <p className="text-[12px] text-faint">
                Signed in with the {cred.kind} · allows {cred.allows}
                {cred.grant ? ` · ${cred.grant}` : ""}
              </p>
            )}
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="ghost" onClick={() => void load()}>
            Refresh
          </Button>
          <Button
            variant="ghost"
            onClick={() => {
              signOut();
              setState(null);
            }}
          >
            Leave recovery mode
          </Button>
        </div>
      </header>

      {stateErr && (
        <p className="mb-4 rounded-lg border border-line bg-critsoft px-3 py-2.5 text-[13px] text-crit">{stateErr}</p>
      )}

      {/* The answer first: is the database there, and what is not running. */}
      <div className="mb-4 grid gap-3 sm:grid-cols-3">
        <Card title="Platform database">
          {db.reachable ? (
            <Pill tone={String(db.cluster_status).toLowerCase() === "primary" ? "ok" : "warn"}>
              reachable · {db.cluster_status}
            </Pill>
          ) : (
            <Pill tone="crit">unreachable</Pill>
          )}
          {db.error && <p className="mt-2 break-words text-[12px] text-muted">{db.error}</p>}
          {state?.panel?.started_without_database && (
            <p className="mt-2 text-[12px] text-muted">The panel started without it; it returns to full service by itself once the database answers.</p>
          )}
        </Card>
        <Card title="Declared primary">
          {decl.primary ? (
            <>
              <p className="text-[15px] font-[620]">{decl.primary}</p>
              <p className="text-[12px] text-muted">
                succession {(decl.succession ?? []).join(" → ") || "none"} · seq {decl.seq}
              </p>
              <p className="mt-1 text-[11.5px] text-faint">
                This node's copy{decl.recorded_at ? `, recorded ${formatRelative(decl.recorded_at)}` : ""} — not a live answer.
              </p>
            </>
          ) : (
            <p className="text-[13px] text-muted">{decl.note ?? "unknown"}</p>
          )}
        </Card>
        <Card title="Services not running">
          {down.length === 0 ? (
            <Pill tone="ok">all required services running</Pill>
          ) : (
            <ul className="space-y-0.5 text-[13px]">
              {down.map((s) => (
                <li key={s.unit}>
                  <span className="text-crit">●</span> {s.title} <span className="text-faint">({s.unit})</span>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>

      <div className="mb-4 grid gap-3 lg:grid-cols-2">
        <Card title="Galera on this node">
          <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-[13px]">
            <dt className="text-muted">MariaDB</dt>
            <dd>{gal.mariadb_active == null ? `not read: ${gal.unread?.mariadb_active ?? ""}` : gal.mariadb_active ? "running" : "stopped"}</dd>
            <dt className="text-muted">grastate</dt>
            <dd className="font-mono text-[12px]">
              {gal.grastate
                ? `seqno ${gal.grastate.seqno} · safe_to_bootstrap ${gal.grastate.safe_to_bootstrap}`
                : "none"}
            </dd>
            {gal.last_committed !== undefined && (
              <>
                <dt className="text-muted">last committed</dt>
                <dd className="font-mono text-[12px]">{gal.last_committed}</dd>
              </>
            )}
            <dt className="text-muted">cluster address</dt>
            {/* null = galera.cnf could not be read — NOT the empty gcomm:// that means "form a new cluster" */}
            <dd className="break-all font-mono text-[12px]">
              {gal.file_cluster_address == null ? `not read: ${gal.unread?.file_cluster_address ?? ""}` : gal.file_cluster_address || "—"}
            </dd>
          </dl>
          {gal.note && <p className="mt-2 text-[12px] leading-relaxed text-muted">{gal.note}</p>}
          {gal.error && <p className="mt-2 text-[12px] text-crit">{gal.error}</p>}
        </Card>
        <Card title="Peers">
          <ul className="space-y-1 text-[13px]">
            {(peers.galera_peers ?? []).map((p: Json) => (
              <li key={p.address} className="flex items-center justify-between">
                <span className="font-mono text-[12.5px]">{p.address}</span>
                <Pill tone={p.answering ? "ok" : "crit"}>{p.answering ? "answering" : "silent"}</Pill>
              </li>
            ))}
            {(peers.galera_peers ?? []).length === 0 && <li className="text-muted">no Galera peers configured</li>}
          </ul>
          {peers.all_silent_since && (
            <p className="mt-2 text-[12px] text-muted">
              Every peer silent since <When value={peers.all_silent_since} /> ({Math.round(peers.all_silent_for_seconds / 60)} min).
            </p>
          )}
        </Card>
      </div>

      <Card title="Shared storage on this node" className="mb-4">
        <table className="w-full text-[13px]">
          <thead className="text-left text-[11.5px] text-muted">
            <tr>
              <th className="py-1 font-medium">Path</th>
              <th className="py-1 font-medium">Recorded backend</th>
              <th className="py-1 font-medium">Mounted now</th>
              <th className="py-1 font-medium" />
            </tr>
          </thead>
          <tbody>
            {[...(storage.juicefs_volumes ?? []).map((v: Json) => ({ path: v.mountpoint, backend: `JuiceFS volume ${v.volume}`, mounted: v.mounted, serving: v.serving })),
              ...(storage.paths ?? []).map((p: Json) => ({ path: p.path, backend: p.backend, mounted: p.mounted, serving: p.serving }))].map((r) => (
              <tr key={r.path} className="border-t border-line">
                <td className="py-1.5 font-mono text-[12.5px]">{r.path}</td>
                <td className="py-1.5">{r.backend}</td>
                <td className="py-1.5 font-mono text-[12px]">{r.mounted || "nothing"}</td>
                <td className="py-1.5 text-right">
                  <Pill tone={r.serving ? "ok" : "crit"}>{r.serving ? "serving" : "not serving"}</Pill>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <p className="mt-2 text-[11.5px] text-faint">
          Backends are this node's copy of the cluster's record
          {storage.record_written_at ? `, written ${formatRelative(storage.record_written_at)}` : " (no copy on this node)"}; mounts are measured now.
          GlusterFS {storage.glusterfs_retired_here ? "is retired on this node" : "is not retired on this node"}.
        </p>
      </Card>

      <Card title="Recover this node" className="mb-4">
        <p className="mb-3 text-[13px] leading-relaxed text-muted">
          The same run as <code className="font-mono text-fg">ozpanel recover</code>. A diagnosis changes nothing and says
          what recovery would do. Recovery refuses while any peer answers or before every peer has been silent long enough —
          promoting beside a live peer creates a second cluster.
        </p>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="ghost" disabled={busy || run?.running} onClick={() => void start(false)}>
            Diagnose
          </Button>
          <Button variant="accent" disabled={busy || run?.running || !canRecover} onClick={() => setConfirmApply(true)}>
            Recover…
          </Button>
          <label className={`ml-2 flex items-center gap-2 text-[12.5px] ${canForce ? "text-muted" : "text-faint"}`}>
            <input type="checkbox" disabled={!canForce} checked={force && canForce} onChange={(e) => setForce(e.target.checked)} />
            Skip the peer-silence check (only if the peer is destroyed and will never return)
          </label>
        </div>
        <div className="mt-2 text-[12px] text-faint">
          {!canRecover && "This credential allows diagnosis only. "}
          {!canForce &&
            "Skipping the peer check needs a recovery grant that allows force — issued on the primary or a successor, so every use names who authorised it."}
        </div>
        {run && (
          <div className="mt-4">
            <p className="mb-1.5 text-[12.5px] text-muted">
              Run {run.id} · {run.apply ? "recover" : "diagnose"}
              {run.force ? " · forced" : ""} · by {run.by} · started <When value={run.started_at} />{" "}
              {run.running ? (
                <Pill tone="accent">running</Pill>
              ) : run.error ? (
                <Pill tone="crit">refused / failed</Pill>
              ) : (
                <Pill tone="ok">finished</Pill>
              )}
            </p>
            <pre className="max-h-[420px] overflow-auto whitespace-pre-wrap rounded-lg border border-line bg-bg p-3 font-mono text-[12px] leading-relaxed">
              {run.output || "…"}
              {run.error ? `\n\n${run.error}` : ""}
            </pre>
          </div>
        )}
      </Card>

      <Card title="Recovery actions on this node">
        {actions.length === 0 ? (
          <p className="text-[13px] text-muted">None recorded.</p>
        ) : (
          <ul className="space-y-0.5 font-mono text-[12px]">
            {[...actions].reverse().map((a, i) => (
              <li key={i}>
                <When value={a.at} /> · {a.method} {a.path} · {a.status} · from {a.ip}
                {a.database ? "" : " · no database"}
              </li>
            ))}
          </ul>
        )}
      </Card>

      <ConfirmDialog
        open={confirmApply}
        onOpenChange={setConfirmApply}
        title={force ? "Recover this node, skipping the peer check?" : "Recover this node?"}
        body={
          force
            ? "This skips the check that every peer is gone. If a peer is still serving, this creates a second writable cluster — a split brain."
            : "Promotes this node's database if it is not Primary, admits mesh peers to the firewall, restores the declared storage and starts the services systemd refused. It refuses if a peer answers."
        }
        confirmLabel={force ? "Recover anyway" : "Recover"}
        destructive={force}
        onConfirm={() => {
          setConfirmApply(false);
          void start(true);
        }}
      />
    </main>
  );
}

function Card({ title, className, children }: { title: string; className?: string; children: React.ReactNode }) {
  return (
    <Panel className={`p-4 ${className ?? ""}`}>
      <h2 className="mb-2 text-[12px] font-medium tracking-wide text-muted uppercase">{title}</h2>
      {children}
    </Panel>
  );
}
